import type { LiveLocale } from "@adea/i18n";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { apiFetch, setCsrfToken } from "./api.ts";

export type Role = "admin" | "developer" | "member" | "viewer";

export interface CompanyLink {
  slug: string;
  name: string;
  role?: Role;
  url: string;
}

export interface Me {
  person: {
    id: string;
    name: string;
    email: string;
    locale: string | null;
    theme?: "system" | "light" | "dark";
  };
  /** The company of this address; null when the app is opened on an address that isn't a company's. */
  company: { id: string; slug: string; name: string; locale: string; memberCount: number; role: Role } | null;
  companies: CompanyLink[];
  locale: LiveLocale;
  csrfToken: string;
}

export const meQuery = queryOptions({
  queryKey: ["me"] as const,
  queryFn: async ({ signal }) => {
    const me = await apiFetch<Me>("/api/v1/me", { signal });
    setCsrfToken(me.csrfToken);
    return me;
  },
  staleTime: 60_000,
});

/** The signed-in person and company. Only used under the `_app` layout, which loads it first. */
export function useMe(): Me {
  return useSuspenseQuery(meQuery).data;
}

export interface SignInOptions {
  magicLink: boolean;
  passkey: boolean;
  google: boolean;
}

export const signInOptionsQuery = queryOptions({
  queryKey: ["sign-in-options"] as const,
  queryFn: ({ signal }) => apiFetch<SignInOptions>("/api/v1/sign-in-options", { signal }),
  staleTime: Number.POSITIVE_INFINITY,
});
